'use strict'

// OOTB Imports
import {useQuery} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import useMultiSite from '@app/hooks/use-multi-site'

// Custom APIs
import UrlLifeCycleMgr from '../custom-api/url-life-cycle-custom-api'

/**
 * Custom hook to manage the lifecycle of a product URL, handling redirection based on product availability.
 *
 * This hook utilizes access tokens and site information to determine the appropriate URL redirection
 * for a product based on its online status for a specific site. It leverages the `useQuery` hook
 * to asynchronously fetch the product URL lifecycle data.
 *
 * @param {string} productId - The unique identifier of the product for which the URL lifecycle is managed.
 * @returns {Object} - The result of the query, containing the product URL lifecycle data.
 *
 * @example
 * const productUrlData = useProductUrlLifeCycle('12345');
 * // productUrlData will contain the lifecycle information for product with ID '12345'.
 *
 * @remarks
 * - If the product is online for the site, it redirects to the product's variation group PDP.
 * - If the product is offline, it retrieves the primary category for redirection.
 * - This hook should be used in a React component context where `useAccessToken` and `useMultiSite` hooks are available.
 */
const useProductUrlLifeCycle = (productId) => {
    const {getTokenWhenReady} = useAccessToken()
    const {site, locale} = useMultiSite()
    /*
        case 1.1 - product is online for the site 
            → redirection to product’s variation group PDP
        case 1.2 - product is offline for the site 
            → retrieve the primary category and use it for redirection (see categories part)
    */
    return useQuery({
        queryKey: ['productUrlLifeCycle', productId],
        queryFn: async () => {
            let result = null
            if (isServer && productId) {
                const accessToken = await getTokenWhenReady()
                const {app: appConfig} = getConfig()
                const api = new UrlLifeCycleMgr({
                    parameters: {
                        siteId: site.id,
                        organizationId: appConfig.commerceAPI.parameters.organizationId,
                        locale: locale.id
                    }
                })
                result = await api.productUrlLifeCycle(site.id, accessToken, {
                    productId: productId
                })
            }

            return result
        }
    })
}

export default useProductUrlLifeCycle
